import type { QuickComposeEntrySourceType } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import type { QuickComposeViewType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import type { CalendarEvent } from 'owa-calendar-types';
import EventScope from 'owa-service/lib/contract/EventScope';
import shouldShowTimeSuggestions from '../../selectors/shouldShowTimeSuggestions';
import initFreeBusyCache from '../../utils/initFreeBusyCache';
import isEntrySourceUndo from '../../utils/isEntrySourceUndo';
import { dismissUndoNotification } from 'owa-calendar-actions';
import { lazyInitializeCalendarChannelPicker } from 'owa-calendar-channel-picker';
import { initializeLocationMetrics } from '../../mutators/eventMetricsMutators';
import {
    addComposeStore,
    getComposeEvent,
    getComposeViewState,
    getQuickComposeViewState,
    setOpenedForm,
    setMetricsInfo,
    setEvent,
    setViewState,
    setOriginalEvent,
} from 'owa-calendar-compose-store-manager';
import { initializeTimeSuggestions } from 'owa-calendar-time-suggestions';
import initializeLocationFeedbackManagerState from 'owa-feedback-manager-actions/lib/actions/initializeLocationFeedbackManagerState';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { QUICK_COMPOSE_FORM_TYPE } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import {
    logComposeFormOpenedTimes,
    initializeTimeSuggestionMetricsInfo,
} from 'owa-calendar-compose-form-actions';
import getCalendarComponent from '../../utils/getCalendarComponent';
import { onShowQuickCompose } from '../../actions/formOpenCloseNotificationActions';
import { initializeEventsForEdit } from '../../utils/initializeEventsForEdit';
import createQuickComposeMetricsInfo from '../../utils/createQuickComposeMetricsInfo';
import createQuickComposeViewState from '../../utils/createQuickComposeViewState';
import { isConnectedAccount } from 'owa-accounts-store';
import { lazyInitializeCache } from 'owa-recipient-cache';
import { setSurfaceAnchor } from '../../mutators/setSurfaceAnchorMutator';

export async function showQuickCompose(
    formId: string,
    anchor: HTMLDivElement | string, //sets the target point that the callout it anchored to
    entrySource: QuickComposeEntrySourceType,
    viewType?: QuickComposeViewType,
    initialCalendarEvent?: Partial<CalendarEvent>,
    eventScope: EventScope = EventScope.Default
) {
    if (isEntrySourceUndo(entrySource)) {
        // For undo scenarios the store already exists, update with the anchor
        // and opened form so that the form will display again

        setSurfaceAnchor(formId, anchor);

        setOpenedForm(formId, QUICK_COMPOSE_FORM_TYPE);
    } else {
        // For non-undo scenarios, generate the new event or fetch the existing
        // event to edit and initialize a new store

        // If the form's entry source is undo, the notification is dismissed when the undo button is
        // selected. In all other cases, we dismiss the notification manually
        dismissUndoNotification();

        addComposeStore(formId);

        const fetchSource = 'QuickCompose_' + entrySource;

        const { originalEvent, event } = await initializeEventsForEdit(
            fetchSource,
            eventScope ?? EventScope.Default,
            initialCalendarEvent?.ItemId ?? undefined,
            initialCalendarEvent
        );

        if (!originalEvent || !event) {
            // TODO: Graceful error handling when the event is not found
            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/119628
            return null;
        }

        setOriginalEvent(formId, originalEvent);
        setEvent(formId, event);

        setViewState(formId, createQuickComposeViewState(event, anchor, viewType));
        setOpenedForm(formId, QUICK_COMPOSE_FORM_TYPE);

        initializeLocationFeedbackManagerState();

        if (!isConnectedAccount(event.ParentFolderId.mailboxInfo.userIdentity)) {
            lazyInitializeCache.importAndExecute();
        }
    }

    // Reinitialize the metrics info for both undo and non-undo scenarios
    setMetricsInfo(formId, createQuickComposeMetricsInfo(entrySource));

    // Initialize FreeBusy Cache if needed
    if (!isConsumer()) {
        initFreeBusyCache(formId);
    }

    initializeLocationMetrics(formId);

    if (shouldShowTimeSuggestions(formId)) {
        // If the suggestions flight is enabled, only then import the suggestions code
        initializeTimeSuggestions.importAndExecute(
            getComposeEvent(formId),
            getComposeViewState(formId).shouldAdjustStartTimeEndTime,
            getCalendarComponent(formId) /* calendarComponent */,
            formId
        );
        initializeTimeSuggestionMetricsInfo(formId);
    }

    lazyInitializeCalendarChannelPicker.importAndExecute(getComposeEvent(formId), formId);

    logComposeFormOpenedTimes(formId);

    // Notify listeners that quick compose is ready, must be last call in this method
    onShowQuickCompose(formId, getQuickComposeViewState(formId).viewType);
}
